<template>
<div class="hris-page">
  <div class="hris-page-inner">
    <div class="hris-page-header hris-page-header-row">
      <div>
        <span class="hris-page-eyebrow">System Settings</span>
        <h1 class="hris-page-title">SaaS Owner Dashboard</h1>
        <p class="hris-page-subtitle">Track tenants, their companies, and enabled feature toggles from one place.</p>
      </div>
      <q-btn
        unelevated
        class="hris-q-outline"
        icon="fas fa-sync-alt"
        label="Refresh"
        :loading="loading"
        @click="fetchDashboard"
      ></q-btn>
    </div>

    <q-linear-progress
      v-if="loading"
      indeterminate
      color="green"
      class="q-mb-md"
    ></q-linear-progress>

    <div class="hris-grid" style="margin-bottom: 20px;">
      <div
        class="hris-stat-card"
        v-for="card in summaryCards"
        :key="card.label"
      >
        <p class="hris-stat-label">{{ card.label }}</p>
        <p class="hris-stat-value">{{ card.value }}</p>
      </div>
    </div>

    <div class="hris-panel">
      <div class="row items-center justify-between q-mb-md">
        <div>
          <h2 class="hris-panel-title" style="margin-bottom: 4px;">Tenant Overview</h2>
          <p class="hris-page-subtitle" style="margin: 0;">Companies and feature toggles grouped by tenant.</p>
        </div>
        <span class="hris-badge hris-badge-accent" v-if="tenants.length">{{ tenants.length }} tenant(s)</span>
      </div>

      <div class="hris-empty" v-if="!tenants.length && !loading">
        No tenant data available yet.
      </div>

      <q-list v-else>
        <q-expansion-item
          v-for="tenant in tenants"
          :key="tenant.id"
          dense
          expand-separator
          class="shadow-0"
        >
          <template v-slot:header>
            <div class="row items-center justify-between" style="width: 100%;">
              <div class="row items-center q-gutter-sm">
                <q-avatar size="40px" class="bg-grey-3">
                  <img v-if="tenant.logo_url" :src="tenant.logo_url" alt="Tenant logo" />
                  <span v-else>{{ avatarInitials(tenant) }}</span>
                </q-avatar>
                <div>
                  <div class="text-body1 text-weight-medium">{{ tenant.name }}</div>
                  <div class="row items-center q-gutter-sm">
                    <div class="text-caption text-grey">Slug: {{ tenant.slug }}</div>
                  </div>
                  <div class="row items-center q-gutter-sm enrolled-count-row">
                    <span class="text-caption text-grey enrolled-employee-label">Enrolled employees</span>
                    <span class="text-h6 text-weight-bold enrolled-employee-value">
                      {{ tenant.enrolled_employee_count || 0 }}
                    </span>
                  </div>
                  <div class="row items-center q-gutter-sm active-count-row">
                    <span class="text-caption text-grey active-employee-label">Active employees</span>
                    <span class="text-h6 text-weight-bold active-employee-value">
                      {{ tenant.active_employee_count || 0 }}
                    </span>
                  </div>
                  <div class="text-caption text-grey">
                    Storage: {{ tenant.storage_path || 'N/A' }}
                  </div>
                </div>
              </div>
              <span class="hris-badge" :class="tenant.is_active === 'Y' ? 'hris-badge-success' : 'hris-badge-neutral'">
                {{ tenant.is_active === 'Y' ? 'Active' : 'Inactive' }}
              </span>
            </div>
          </template>

          <div class="row q-col-gutter-md q-mt-md">
            <div class="col-12 col-md-6">
              <div class="text-caption text-grey">Companies (active / total)</div>
              <div class="row wrap q-gutter-sm q-mt-xs">
                <q-chip
                  v-for="company in tenant.companies"
                  :key="company.id"
                  dense
                  :color="company.is_active === 'Y' ? 'green' : 'grey-3'"
                  :text-color="company.is_active === 'Y' ? 'white' : 'black'"
                  class="text-caption"
                >
                  {{ company.name }}
                </q-chip>
                <div v-if="!tenant.companies.length" class="text-caption text-grey q-mt-xs">
                  No companies assigned.
                </div>
              </div>
            </div>
            <div class="col-12 col-md-6">
              <div class="text-caption text-grey">Feature toggles (enabled)</div>
              <div class="row wrap q-gutter-sm q-mt-xs">
                <q-chip
                  v-for="feature in tenant.features"
                  :key="feature.slug"
                  dense
                  :color="feature.enabled ? 'green' : 'grey-3'"
                  :text-color="feature.enabled ? 'white' : 'black'"
                >
                  <q-icon
                    v-if="feature.enabled"
                    name="fas fa-check"
                    size="14px"
                    class="q-mr-xs"
                  ></q-icon>
                  {{ feature.name }}
                </q-chip>
                <div v-if="!tenant.features.length" class="text-caption text-grey q-mt-xs">
                  Features have not been configured yet.
                </div>
              </div>
            </div>
          </div>
        </q-expansion-item>
      </q-list>
    </div>
  </div>
</div>
</template>

<script>
import axios from 'axios';
import mixins from '../../mixins';

export default {
  mixins: [mixins],
  data() {
    return {
      loading: false,
      dashboard: {
        tenants: []
      }
    };
  },
  computed: {
    tenants() {
      return this.dashboard.tenants || [];
    },
    summaryCards() {
      const tenantCount = this.tenants.length;
      const totalCompanies = this.tenants.reduce((total, tenant) => total + (tenant.company_count || 0), 0);
      const activeCompanies = this.tenants.reduce((total, tenant) => total + (tenant.active_company_count || 0), 0);
      const enabledFeatures = this.tenants.reduce((total, tenant) => total + ((tenant.features || []).filter(f => f.enabled).length), 0);
      const firstTenantFeatures = (this.tenants[0] && this.tenants[0].features) || [];
      const availableFeatures = firstTenantFeatures.length;

      return [
        { label: 'Tenants', value: tenantCount, icon: 'fas fa-building' },
        { label: 'Companies', value: totalCompanies, icon: 'fas fa-sitemap' },
        { label: 'Active Companies', value: activeCompanies, icon: 'fas fa-check-circle' },
        {
          label: 'Feature toggles enabled',
          value: availableFeatures ? `${enabledFeatures} / ${availableFeatures}` : `${enabledFeatures}`,
          icon: 'fas fa-toggle-on'
        }
      ];
    }
  },
  methods: {
    async fetchDashboard() {
      this.loading = true;
      try {
        const response = await axios.create({
          baseURL: this.apiUrl,
          headers: {
            Authorization: `Bearer ${this.token}`
          }
        }).get('/admin/tenant-dashboard');

        this.dashboard = response.data && response.data.data ? response.data.data : { tenants: [] };
      } catch (error) {
        this.$q.notify({
          color: 'negative',
          message: (error.response && error.response.data && error.response.data.message) || 'Unable to load tenant data.',
          position: 'top'
        });
      } finally {
        this.loading = false;
      }
    },
    avatarInitials(tenant) {
      if (tenant.initials) {
        return tenant.initials;
      }
      if (!tenant.name) {
        return '?';
      }
      const matches = tenant.name.match(/\b\w/g) || [];
      return (matches.slice(0, 2).join('') || tenant.name.substr(0, 2)).toUpperCase();
    }
  },
  mounted() {
    this.fetchDashboard();
  }
};
</script>

<style scoped>
.active-count-row {
  gap: 0.5rem;
}

.active-employee-label {
  letter-spacing: 0.04em;
  font-size: 0.75rem;
  text-transform: uppercase;
}

.active-employee-value {
  margin-top: 0;
  margin-bottom: 0;
}
</style>
